import { Show, useRecordContext } from 'react-admin';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { EmptyHint, FieldRow, Section } from '../../components/Section';
import { StatusChip } from '../../components/StatusChip';
import { UserLink } from '../../components/EntityLink';
import { HideInvitationButton } from './InvitationActions';
import { ModeratePhotoButtons } from '../photos/PhotoActions';
import {
  formatDateTime,
  invitationCategoryLabels,
  invitationStatusLabels,
  label,
  matchStatusLabels,
  paymentTypeLabels,
  photoStatusLabels,
} from '../../i18n/labels';
import type { InvitationDetail } from '../../types';

function InvitationTitle(): JSX.Element {
  const record = useRecordContext<InvitationDetail>();
  return (
    <span>
      {record
        ? `Приглашение: ${label(invitationCategoryLabels, record.category)} — ${record.place.name}`
        : ''}
    </span>
  );
}

function InvitationShowLayout(): JSX.Element | null {
  const record = useRecordContext<InvitationDetail>();
  if (!record) return null;

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Section title="Приглашение" action={<HideInvitationButton />}>
        <Typography variant="h6" sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
          {label(invitationCategoryLabels, record.category)}
          <StatusChip status={record.status} dict={invitationStatusLabels} />
        </Typography>
        <FieldRow
          label="Автор"
          value={<UserLink id={record.author.id} label={record.author.displayName} />}
        />
        <FieldRow label="Место" value={`${record.place.name} — ${record.place.address}`} />
        <FieldRow label="Когда встреча" value={formatDateTime(record.scheduledAt)} />
        <FieldRow label="Оплата" value={label(paymentTypeLabels, record.paymentType)} />
        <FieldRow label="Описание" value={record.description} />
        <FieldRow label="Создано" value={formatDateTime(record.createdAt)} />
        {record.archivedAt ? (
          <FieldRow label="В архиве с" value={formatDateTime(record.archivedAt)} />
        ) : null}
        <FieldRow
          label="Реакции"
          value={`${record.likesCount} лайков · ${record.dislikesCount} дизлайков · ${record.matchesCount} матчей`}
        />
      </Section>

      <Section title={`Фото (${record.photos.length})`}>
        {record.photos.length === 0 ? (
          <EmptyHint text="Фото не загружены" />
        ) : (
          <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {record.photos.map((p) => (
              <Box key={p.id} sx={{ width: 240 }}>
                <img
                  src={p.photoUrl}
                  alt={`Фото ${p.position + 1}`}
                  style={{ width: '100%', borderRadius: 8, display: 'block' }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  <StatusChip status={p.status} dict={photoStatusLabels} />
                  <ModeratePhotoButtons photoId={p.id} status={p.status} />
                </Box>
                {p.rejectionReason ? (
                  <Typography variant="caption" color="error">
                    Причина: {p.rejectionReason}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Stack>
        )}
      </Section>

      <Section title={`Матчи (последние ${record.matches.length} из ${record.matchesCount})`}>
        {record.matches.length === 0 ? (
          <EmptyHint text="Матчей нет" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Гость</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Встреча подтверждена</TableCell>
                <TableCell>Создан</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {record.matches.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>
                    <UserLink id={m.guest.id} label={m.guest.displayName} />
                  </TableCell>
                  <TableCell>
                    <StatusChip status={m.status} dict={matchStatusLabels} />
                  </TableCell>
                  <TableCell>{formatDateTime(m.meetingConfirmedAt)}</TableCell>
                  <TableCell>{formatDateTime(m.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </Stack>
  );
}

export function InvitationShow(): JSX.Element {
  return (
    <Show title={<InvitationTitle />} component="div">
      <InvitationShowLayout />
    </Show>
  );
}
